import * as React from 'react';

import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';

import { customPriceListDuplicateAtom } from '../../../_graphql/mutationCustomPriceListLifecycle';
import { useTranslations } from '../../../i18n/useTranslations';

export const useDuplicateCustomPriceList = (
  onSuccess: (newId: string) => void,
) => {
  const t = useTranslations();
  const toast = useToast();

  const [isOpen, setIsOpen] = React.useState(false);
  const [sourceId, setSourceId] = React.useState<string | null>(null);
  const [duplicateName, setDuplicateName] = React.useState('');
  const [isDuplicating, setIsDuplicating] = React.useState(false);

  const duplicateAction = useSetAtom(customPriceListDuplicateAtom({}));

  const open = React.useCallback((id: string, name: string) => {
    setSourceId(id);
    setDuplicateName(`${name} (Copy)`);
    setIsOpen(true);
  }, []);

  const close = React.useCallback(() => {
    setIsOpen(false);
    setSourceId(null);
  }, []);

  const handleSubmit = React.useCallback(async () => {
    if (!sourceId || !duplicateName.trim()) return;
    setIsDuplicating(true);
    try {
      const result = await duplicateAction({
        id: sourceId,
        name: duplicateName.trim(),
      });
      const newId = result?.pricingCustomPriceListDuplicate?.id;
      toast.success(t.pricing.customPriceLists.toast.created);
      close();
      if (newId) {
        onSuccess(newId);
      }
    } catch (err) {
      toast.error(
        t.pricing.customPriceLists.toast.createError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsDuplicating(false);
    }
  }, [sourceId, duplicateName, duplicateAction, toast, t, close, onSuccess]);

  return {
    duplicateName,
    setDuplicateName,
    isDuplicating,
    handleSubmitDuplicate: handleSubmit,
    dialogProps: {
      isOpen,
      close,
    },
    open,
  };
};
